import {Component, inject, OnInit} from '@angular/core';
import {
    DxDataGridComponent, DxTemplateDirective, DxFormComponent, DxButtonComponent, DxTabsComponent
} from "devextreme-angular";
import {
    DxiColumnComponent, DxiItemComponent, DxoColumnChooserComponent, DxoExportComponent, DxoFilterRowComponent,
    DxoHeaderFilterComponent, DxoLoadPanelComponent, DxoPagerComponent,
    DxoPagingComponent, DxoScrollingComponent, DxoSortingComponent,
    DxoToolbarComponent
} from "devextreme-angular/ui/nested";
import {
    HuddleStatus,
    HuddleGridViewDto, MarkAsLateCommand, EmployeesClient, UndoLateCommand
} from "@services";
import {DxDataGridTypes} from "devextreme-angular/ui/data-grid";
import EnumUtils from "../../../shared/enum-utils";
import {UserAvatarEmailComponent} from "../../../components/utils/user-avatar-email/user-avatar-email.component";
import {FilterPanelComponent} from "../../../components/library/filter-panel/filter-panel.component";
import {CapitalCasePipe} from "../../../pipes/capital-case.pipe";
import {ContactStatusComponent} from "../../../components";
import {DasherizePipe} from "../../../pipes/dasherize.pipe";
import notify from "devextreme/ui/notify";
import {FilterConfig, GridPageComponent} from "../../common/grid-page.component";

interface Shift {
    code: string | null;
    text: string;
}

@Component({
    selector: 'app-huddles',
    imports: [
        DxDataGridComponent,
        DxiColumnComponent,
        DxoHeaderFilterComponent,
        DxoScrollingComponent,
        DxTemplateDirective,
        DxiItemComponent,
        DxoColumnChooserComponent,
        DxoExportComponent,
        DxoLoadPanelComponent,
        DxoSortingComponent,
        DxoToolbarComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoFilterRowComponent,
        UserAvatarEmailComponent,
        DxFormComponent,
        FilterPanelComponent,
        CapitalCasePipe,
        ContactStatusComponent,
        DasherizePipe,
        DxButtonComponent,
        DxTabsComponent
    ],
    templateUrl: './huddles.component.html',
    styleUrls: ['./huddles.component.scss']
})
export class HuddlesComponent extends GridPageComponent<HuddleGridViewDto> implements OnInit {
    private employeeClient = inject(EmployeesClient);

    shifts: Shift[] = [];
    selectedTab: string | null = null;
    dataSource = this.dataSources.huddles();
    isQuickViewPanelOpened: boolean = false;
    timeLogId: string;
    selectedTimeLog: HuddleGridViewDto | null = null;
    huddleStatusOptions = EnumUtils.getEnumOptions(HuddleStatus);
    private undoableEmployees: Set<string> = new Set<string>();
    protected override exportName: string = "Huddles";

    protected override filterConfig: FilterConfig[] = [
        { field: 'employeeName', op: 'contains' },
        { field: 'huddleStatus', op: '=' }
    ];

    constructor() {
        super();
        this.init(HuddleGridViewDto);
    }

    ngOnInit(): void {
        this.getAllHuddleShifts();
    }

    override async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        this.timeLogId = e.data.id;
        this.selectedTimeLog = {...e.data} as HuddleGridViewDto;
        this.isQuickViewPanelOpened = true;
    }

    isUndoable(employeeId: string): boolean {
        return this.undoableEmployees.has(employeeId);
    }

    markAsLate = (employeeId: string) => {
        let markAsLateCommand = new MarkAsLateCommand({employeeId: employeeId})
        this.employeeClient.markAsLate(markAsLateCommand).subscribe({
            next: async () => {
                this.undoableEmployees.add(employeeId);
                await this.dataGrid.instance.refresh()
            },
            error: (errors: Array<any>) => {
                this.undoableEmployees.delete(employeeId);
                errors.forEach((errMsg => {
                    notify({
                        message: errMsg.message,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                }));
            }
        });
    }

    undoLate = (employeeId: string) => {
        let undoLateCommand = new UndoLateCommand({employeeId: employeeId})
        this.employeeClient.undoLate(undoLateCommand).subscribe({
            next: async () => {
                this.undoableEmployees.delete(employeeId);
                await this.dataGrid.instance.refresh()
            },
            error: (errors: Array<any>) => {
                errors.forEach((errMsg => {
                    notify({
                        message: errMsg.message,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                }));
            }
        });
    }

    onShiftButtonClick(code: string) {
        this.selectedTab = code;
        if (code) {
            this.dataGrid.instance.filter([this.gridFieldName('shiftCode'), '=', code]);
        } else {
            this.dataGrid.instance.clearFilter();
        }
    }

    private getAllHuddleShifts(): void {
        const loadOptions = {
            group: [{
                selector: 'shiftCode',
                isExpanded: false
            }]
        };

        this.dataSource.load(loadOptions).then((data: any[]) => {
            const dynamicShifts: Shift[] = data
                .filter(group => group.key != null && group.key !== '')
                .map((group: any) => ({
                    code: group.key,
                    text: `Shift ${group.key}`
                }));

            this.shifts = [
                {code: null, text: 'All Shifts'},
                ...dynamicShifts
            ];
        });
    }
}
